/**
 * Users module API wrapper.
 * Feature code imports from THIS file, never from _generated/.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";

import type { APIError } from "../http.js";
import { qk } from "../query-keys.js";

import { apiRequest } from "./_client.js";

// ── Types ────────────────────────────────────────────────────────────

export type UserRole =
  | "tenant_admin"
  | "ops_manager"
  | "store_manager"
  | "store_associate"
  | "field_auditor"
  | "viewer";

export interface CompanyMembership {
  tenantId: string;
  tenantName: string;
  role: UserRole;
  isDefault: boolean;
}
export interface CompaniesListResponse {
  items: CompanyMembership[];
}

export interface TenantUserInfo {
  companyUserId: string;
  userId: string;
  email: string;
  fullName: string | null;
  role: UserRole;
  isDefault: boolean;
  createdAt: string;
}
export interface TenantUsersListResponse {
  items: TenantUserInfo[];
}

/**
 * Role-scope classification mirroring `packages/contracts` ROLE_SCOPE.
 * Single source of truth on the client for "do I show the store picker?".
 */
export const ROLE_SCOPE: Record<UserRole, "tenant" | "store"> = {
  tenant_admin: "tenant",
  ops_manager: "tenant",
  viewer: "tenant",
  store_manager: "store",
  store_associate: "store",
  field_auditor: "store",
};

export const isTenantWideRole = (r: UserRole) => ROLE_SCOPE[r] === "tenant";
export const isStoreScopedRole = (r: UserRole) => ROLE_SCOPE[r] === "store";

export interface UserStoresResponse {
  mode: "all" | "scoped";
  storeIds: string[];
}

export interface UnboundUser {
  userId: string;
  email: string;
  role: UserRole;
}

export interface UnboundUsersResponse {
  items: UnboundUser[];
}

// ── Endpoint wrappers ────────────────────────────────────────────────

export const usersApi = {
  me: (signal?: AbortSignal) =>
    apiRequest<{ id: string; email: string; tenantId: string; role: string }>({
      method: "GET",
      url: "/api/v2/users/me",
      signal,
    }),

  myCompanies: (signal?: AbortSignal): Promise<CompaniesListResponse> =>
    apiRequest({ method: "GET", url: "/api/v2/users/me/companies", signal }),

  listTenantUsers: (companyId: string, signal?: AbortSignal): Promise<TenantUsersListResponse> =>
    apiRequest({
      method: "GET",
      url: `/api/v2/users/companies/${encodeURIComponent(companyId)}/users`,
      signal,
    }),

  updateCompanyUserRole: (companyUserId: string, role: UserRole): Promise<TenantUserInfo> =>
    apiRequest({
      method: "PATCH",
      url: `/api/v2/users/company-users/${encodeURIComponent(companyUserId)}`,
      data: { role },
    }),

  adminResetPassword: (userId: string): Promise<{ ok: true; devLink?: string }> =>
    apiRequest({
      method: "POST",
      url: `/api/v2/users/${encodeURIComponent(userId)}/reset-password`,
    }),

  removeCompanyUser: (companyUserId: string): Promise<{ ok: true }> =>
    apiRequest({
      method: "DELETE",
      url: `/api/v2/users/company-users/${encodeURIComponent(companyUserId)}`,
    }),

  getUserStores: (userId: string, signal?: AbortSignal): Promise<UserStoresResponse> =>
    apiRequest({
      method: "GET",
      url: `/api/v2/users/${encodeURIComponent(userId)}/stores`,
      signal,
    }),

  replaceUserStores: (userId: string, storeIds: string[]): Promise<{ ok: true }> =>
    apiRequest({
      method: "PUT",
      url: `/api/v2/users/${encodeURIComponent(userId)}/stores`,
      data: { storeIds },
    }),

  listStoreScopedUnbound: (signal?: AbortSignal): Promise<UnboundUsersResponse> =>
    apiRequest({
      method: "GET",
      url: "/api/v2/users/store-scoped-unbound",
      signal,
    }),
};

// ── React-Query hooks ────────────────────────────────────────────────

export function useMyCompaniesQuery(
  options?: Omit<UseQueryOptions<CompaniesListResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<CompaniesListResponse, APIError>({
    queryKey: qk.users.myCompanies,
    queryFn: ({ signal }) => usersApi.myCompanies(signal),
    retry: false,
    staleTime: 60_000,
    ...options,
  });
}

export function useTenantUsersQuery(
  companyId: string,
  options?: Omit<UseQueryOptions<TenantUsersListResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<TenantUsersListResponse, APIError>({
    queryKey: qk.users.tenantUsers(companyId),
    queryFn: ({ signal }) => usersApi.listTenantUsers(companyId, signal),
    retry: false,
    staleTime: 30_000,
    ...options,
  });
}

export function useUpdateCompanyUserRoleMutation(
  options?: Omit<
    UseMutationOptions<TenantUserInfo, APIError, { companyUserId: string; role: UserRole }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<TenantUserInfo, APIError, { companyUserId: string; role: UserRole }>({
    ...options,
    mutationFn: ({ companyUserId, role }) => usersApi.updateCompanyUserRole(companyUserId, role),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      // Invalidate all tenantUsers queries (we don't know which tenant this belongs to from the row alone)
      void qc.invalidateQueries({ queryKey: ["users", "tenantUsers"] });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useRemoveCompanyUserMutation(
  options?: Omit<UseMutationOptions<{ ok: true }, APIError, string>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, APIError, string>({
    ...options,
    mutationFn: (companyUserId: string) => usersApi.removeCompanyUser(companyUserId),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      // Drop them from the visible team table on next render.
      void qc.invalidateQueries({ queryKey: ["users", "tenantUsers"] });
      // Their store bindings (if any) just got cascade-archived too.
      void qc.invalidateQueries({ queryKey: qk.users.unbound });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useUserStoresQuery(
  userId: string,
  options?: Omit<UseQueryOptions<UserStoresResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<UserStoresResponse, APIError>({
    queryKey: qk.users.stores(userId),
    queryFn: ({ signal }) => usersApi.getUserStores(userId, signal),
    retry: false,
    enabled: userId !== "",
    ...options,
  });
}

export function useReplaceUserStoresMutation(
  options?: Omit<
    UseMutationOptions<{ ok: true }, APIError, { userId: string; storeIds: string[] }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, APIError, { userId: string; storeIds: string[] }>({
    ...options,
    mutationFn: ({ userId, storeIds }) => usersApi.replaceUserStores(userId, storeIds),
    onSuccess: (data, vars, onMutateResult, ctx) => {
      void qc.invalidateQueries({ queryKey: qk.users.stores(vars.userId) });
      void qc.invalidateQueries({ queryKey: qk.users.unbound });
      options?.onSuccess?.(data, vars, onMutateResult, ctx);
    },
  });
}

export function useUnboundStoreScopedUsersQuery(
  options?: Omit<UseQueryOptions<UnboundUsersResponse, APIError>, "queryKey" | "queryFn">,
) {
  return useQuery<UnboundUsersResponse, APIError>({
    queryKey: qk.users.unbound,
    queryFn: ({ signal }) => usersApi.listStoreScopedUnbound(signal),
    retry: false,
    ...options,
  });
}

export function useAdminResetPasswordMutation(
  options?: Omit<
    UseMutationOptions<{ ok: true; devLink?: string }, APIError, string>,
    "mutationFn"
  >,
) {
  return useMutation<{ ok: true; devLink?: string }, APIError, string>({
    ...options,
    mutationFn: (userId) => usersApi.adminResetPassword(userId),
  });
}
